// extra-fx-generative — generative/algorithmic effects
// Reaction-diffusion (Gray-Scott), Flow-field distortion, Cellular automata, Voronoi stippling

const N = (v) => Number(v) || 0;

// ---- Reaction-diffusion (Gray-Scott) ----
const reactionDiffusion = async (canvas, ctx, steps, feed, kill) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    let a = new Float32Array(w * h);
    let b = new Float32Array(w * h);
    // Seed from image: dark areas get b=1
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        const lum = (0.299*data[i] + 0.587*data[i+1] + 0.114*data[i+2]) / 255;
        a[p] = 1; b[p] = lum < 0.5 ? 0.9 : 0;
    }
    const F = N(feed) / 1000, K = N(kill) / 1000;
    const D = 0.2, dt = 1;
    const n = Math.max(1, Math.floor(N(steps)));
    for (let step = 0; step < n; step++) {
        const na = new Float32Array(w * h), nb = new Float32Array(w * h);
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            const p = y * w + x;
            const lapA = -a[p] + (a[p-1]||a[p]) * 0.2 + (a[p+1]||a[p]) * 0.2 + (a[p-w]||a[p]) * 0.2 + (a[p+w]||a[p]) * 0.2;
            const lapB = -b[p] + (b[p-1]||b[p]) * 0.2 + (b[p+1]||b[p]) * 0.2 + (b[p-w]||b[p]) * 0.2 + (b[p+w]||b[p]) * 0.2;
            na[p] = a[p] + (D * lapA - a[p]*b[p]*b[p] + F*(1-a[p])) * dt;
            nb[p] = b[p] + (D * lapB + a[p]*b[p]*b[p] - (K+F)*b[p]) * dt;
        }
        a = na; b = nb;
    }
    // Map concentration to color, blending with original
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        const val = Math.max(0, Math.min(1, b[p]));
        const tint = Math.floor(val * 255);
        data[i] = (data[i] * (1 - val) + tint * val) | 0;
        data[i+1] = (data[i+1] * (1 - val) + (255 - tint) * val) | 0;
        data[i+2] = (data[i+2] * (1 - val) + tint * 0.5 * val) | 0;
    }
    ctx.putImageData(img, 0, 0);
};

// ---- Flow-field distortion ----
function valueNoise(seed) {
    const perm = new Uint8Array(512);
    let s = seed >>> 0;
    for (let i = 0; i < 256; i++) perm[i] = i;
    for (let i = 255; i > 0; i--) { s = (s * 1664525 + 1013904223) >>> 0; const j = s % (i+1); const t = perm[i]; perm[i] = perm[j]; perm[j] = t; }
    for (let i = 0; i < 256; i++) perm[256+i] = perm[i];
    const fade = t => t*t*t*(t*(t*6-15)+10);
    const lerp = (a,b,t) => a + (b-a)*t;
    return (x, y) => {
        const xi = Math.floor(x) & 255, yi = Math.floor(y) & 255;
        const xf = x - Math.floor(x), yf = y - Math.floor(y);
        const a = perm[xi + perm[yi]], b = perm[xi+1 + perm[yi]];
        const c = perm[xi + perm[yi+1]], d = perm[xi+1 + perm[yi+1]];
        const u = fade(xf), v = fade(yf);
        return lerp(lerp(a, b, u), lerp(c, d, u), v) / 255;
    };
}
const flowField = async (canvas, ctx, scale, strength) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const src = new Uint8ClampedArray(img.data);
    const sc = Math.max(0.001, N(scale) / 1000);
    const str = N(strength);
    const noise = valueNoise(Math.floor(Math.random() * 1e9));
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const angle = noise(x * sc, y * sc) * Math.PI * 4;
        const dx = Math.cos(angle) * str, dy = Math.sin(angle) * str;
        const sx = Math.max(0, Math.min(w-1, Math.round(x + dx)));
        const sy = Math.max(0, Math.min(h-1, Math.round(y + dy)));
        const si = (sy * w + sx) * 4, di = (y * w + x) * 4;
        img.data[di] = src[si]; img.data[di+1] = src[si+1]; img.data[di+2] = src[si+2]; img.data[di+3] = 255;
    }
    ctx.putImageData(img, 0, 0);
};

// ---- Cellular automata (Conway's Life seeded from image) ----
const cellularAutomata = async (canvas, ctx, gens, blend) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    let grid = new Uint8Array(w * h);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        grid[p] = (0.299*data[i] + 0.587*data[i+1] + 0.114*data[i+2]) < 128 ? 1 : 0;
    }
    const n = Math.max(1, Math.floor(N(gens)));
    for (let g = 0; g < n; g++) {
        const next = new Uint8Array(w * h);
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            const p = y * w + x;
            let count = 0;
            for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
                if (dx === 0 && dy === 0) continue;
                const nx = (x + dx + w) % w, ny = (y + dy + h) % h;
                count += grid[ny * w + nx];
            }
            if (grid[p]) next[p] = count === 2 || count === 3 ? 1 : 0;
            else next[p] = count === 3 ? 1 : 0;
        }
        grid = next;
    }
    const bl = N(blend) / 100;
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        const v = grid[p] ? 255 : 0;
        data[i] = (data[i] * (1-bl) + v * bl) | 0;
        data[i+1] = (data[i+1] * (1-bl) + v * bl) | 0;
        data[i+2] = (data[i+2] * (1-bl) + v * bl) | 0;
    }
    ctx.putImageData(img, 0, 0);
};

// ---- Voronoi stippling ----
const voronoiStipple = async (canvas, ctx, dots, size) => {
    const w = canvas.width, h = canvas.height;
    const img = ctx.getImageData(0, 0, w, h);
    const data = img.data;
    const numDots = Math.max(10, Math.floor(N(dots)));
    const dotSize = Math.max(0.5, N(size) / 10);
    // Weighted random points (more dots in dark areas)
    const points = [];
    for (let i = 0; i < numDots; i++) {
        let bx, by, best = -1;
        for (let t = 0; t < 10; t++) {
            const x = Math.floor(Math.random() * w), y = Math.floor(Math.random() * h);
            const idx = (y * w + x) * 4;
            const lum = 255 - (0.299*data[idx] + 0.587*data[idx+1] + 0.114*data[idx+2]);
            if (lum > best) { best = lum; bx = x; by = y; }
        }
        points.push({ x: bx, y: by, w: best });
    }
    // Fill white background
    for (let i = 0; i < data.length; i += 4) { data[i] = 255; data[i+1] = 255; data[i+2] = 255; data[i+3] = 255; }
    // Draw dots
    for (const pt of points) {
        const r = dotSize * (pt.w / 255) * 2;
        const r2 = r * r;
        for (let dy = -Math.ceil(r); dy <= Math.ceil(r); dy++) for (let dx = -Math.ceil(r); dx <= Math.ceil(r); dx++) {
            if (dx*dx + dy*dy > r2) continue;
            const x = Math.max(0, Math.min(w-1, pt.x + dx)), y = Math.max(0, Math.min(h-1, pt.y + dy));
            const idx = (y * w + x) * 4;
            const dist = Math.sqrt(dx*dx + dy*dy) / r;
            const v = Math.max(0, 255 - dist * 255 * 2);
            data[idx] = Math.min(data[idx], 255 - v);
            data[idx+1] = Math.min(data[idx+1], 255 - v);
            data[idx+2] = Math.min(data[idx+2], 255 - v);
        }
    }
    ctx.putImageData(img, 0, 0);
};

const descriptors = [
    { name: 'gen-reaction-diffusion', inputs: [
        { name: 'Steps', type: 'knob', min: 1, max: 200, step: 1, value: 50 },
        { name: 'Feed', type: 'knob', min: 1, max: 20, step: 1, value: 5 },
        { name: 'Kill', type: 'knob', min: 1, max: 20, step: 1, value: 6 }], fn: reactionDiffusion },
    { name: 'gen-flow-field', inputs: [
        { name: 'Scale', type: 'knob', min: 1, max: 100, step: 1, value: 20 },
        { name: 'Strength', type: 'knob', min: 1, max: 100, step: 1, value: 30 }], fn: flowField },
    { name: 'gen-cellular', inputs: [
        { name: 'Gens', type: 'knob', min: 1, max: 50, step: 1, value: 10 },
        { name: 'Blend', type: 'knob', min: 0, max: 100, step: 1, value: 50 }], fn: cellularAutomata },
    { name: 'gen-voronoi-stipple', inputs: [
        { name: 'Dots', type: 'knob', min: 100, max: 5000, step: 10, value: 1000 },
        { name: 'Size', type: 'knob', min: 1, max: 30, step: 1, value: 10 }], fn: voronoiStipple },
];

const lib = {};
descriptors.forEach(d => lib[d.name] = d.fn);

module.exports = { descriptors, lib };
